import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/material/styles';
import { faerbungCategoryTextMap } from '../../../survey/editor/FaerbungReviewSettings';
import {
  DigitalCase,
  FaerbungReviewMeta,
  FaerbungReviewScore,
  PartialBy,
} from '../../../../redux/types';
import { realNumberRegex } from '../../../../utilities/regex';

type EditProps = {
  updated?: boolean;
};

type EditState = {
  [caseId: number]: PartialBy<FaerbungReviewScore, 'id'> & EditProps;
};

type Props = {
  caseList: DigitalCase[];
  faerbungReviewMeta: FaerbungReviewMeta;
  faerbungScoresByCaseId: EditState;
  enrollmentId: number;
  editMode: boolean;
  dispatch?: React.Dispatch<ActionTypes>;
  onCellClick?: (dgtlCase: DigitalCase, category: FaerbungReviewMeta['categories'][0]) => void;
};

type CellProps = {
  clickable: 'true'| 'false',
};

type ActionTypes = { type: 'update', payload: FaerbungReviewScore & EditProps } |
  { type: 'add', payload: Omit<FaerbungReviewScore, 'id'> & EditProps} |
  { type: 'resetState', payload: Record<number, FaerbungReviewScore> };

export function faerbungTableReducer(state: EditState, action: ActionTypes): EditState {
  switch (action.type) {
    case 'resetState': {
      return action.payload;
    }
    case 'add': {
      return {
        ...state,
        [action.payload.digitalCaseId]: action.payload,
      };
    }
    case 'update': {
      return {
        ...state,
        [action.payload.digitalCaseId]: action.payload,
      };
    }
    default:
      return state;
  }
}

const StyledTableCell = styled<any>(TableCell)((
  props: CellProps & TableCellProps,
) => {
  if (props.clickable === 'true') {
    return {
      lineBreak: 'strict',
      minWidth: '155px',
      '&:hover': {
        backgroundColor: 'ButtonFace',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      },
    };
  }
  return {
    lineBreak: 'strict',
    minWidth: '155px',
  };
});

const FaerbungReviewTable = ({
  caseList,
  faerbungReviewMeta,
  faerbungScoresByCaseId: state,
  editMode,
  dispatch,
  enrollmentId,
  onCellClick,
}: Props) => {
  const cellValue = (c: DigitalCase, metaCat: FaerbungReviewScore['categoryScore'][0]) => {
    const score = state[c.id]?.categoryScore
      .find((cs) => cs.category === metaCat.category)?.score !== undefined
      ? state[c.id]?.categoryScore
        .find((cs) => cs.category === metaCat.category)?.score : '-';
    return score;
  };

  const textFieldValue = (c: DigitalCase, cat: FaerbungReviewScore['categoryScore'][0]) => {
    const score = state[c.id]?.categoryScore
      .find((cs) => cs.category === cat.category)?.score !== undefined
      ? state[c.id]?.categoryScore
        .find((cs) => cs.category === cat.category)?.score : '';
    return score;
  };

  const scoreChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, c: DigitalCase, metacat: FaerbungReviewScore['categoryScore'][0]) => {
    if (!dispatch) {
      return;
    }
    const text = e.target.value;
    const test = realNumberRegex.test(text);
    const existingRecord = state[c.id];
    let enteredScore = 0;
    if (test && metacat.score >= Number(text)) {
      enteredScore = Number(text);
    }
    if (existingRecord && existingRecord.id) {
      // update record
      const recObj: FaerbungReviewScore & EditProps = {
        ...existingRecord,
        id: existingRecord.id,
        categoryScore: existingRecord.categoryScore
          .map((cs) => (cs.category === metacat.category ? { ...cs, score: enteredScore } : cs)),
        updated: true,
      };
      recObj.score = recObj.categoryScore.reduce((a, cur) => (a + cur.score), 0);
      dispatch({
        type: 'update',
        payload: recObj,
      });
    } else {
      // add new record
      const recObj: Omit<FaerbungReviewScore, 'id'> & EditProps = {
        digitalCaseId: c.id,
        digitalSectionId: c.digitalSectionId,
        digitalSurveyId: c.digitalSurveyId,
        enrollmentId,
        categoryScore: (() => {
          if (existingRecord?.categoryScore?.length === faerbungReviewMeta.categories.length) {
            return existingRecord.categoryScore
              .map((er) => ((
                er.category === metacat.category)
                ? { ...er, score: enteredScore } : er));
          }
          return faerbungReviewMeta.categories
            .map((fmc) => (
              fmc.category === metacat.category
                ? { ...fmc, score: enteredScore } : { ...fmc, score: 0 }
            ));
        })(),
        updated: true,
        score: 0,
      };
      recObj.score = recObj.categoryScore.reduce((a, cur) => (a + cur.score), 0);
      dispatch({
        type: 'add',
        payload: recObj,
      });
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell><FormattedMessage id="faerbung.criteria" /></TableCell>
            {
                caseList.map((c) => (
                  <TableCell align="center" key={c.id}>
                    {c.name}
                  </TableCell>
                ))
              }
          </TableRow>
        </TableHead>
        <TableBody>
          {
              faerbungReviewMeta.categories.map((metaCat) => (
                <TableRow
                  key={metaCat.category}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      position: 'sticky',
                      backgroundColor: 'white',
                      left: 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <FormattedMessage id={faerbungCategoryTextMap[metaCat.category]} />
                    </Typography>
                  </TableCell>
                  {
                    caseList.map((c) => (
                      <StyledTableCell
                        key={c.id}
                        align="center"
                        onClick={onCellClick ? () => onCellClick(c, metaCat) : undefined}
                        clickable={onCellClick ? 'true' : 'false'}
                      >
                        {
                          !editMode && (
                            cellValue(c, metaCat)
                          )
                        }
                        {
                          editMode && (
                            <TextField
                              sx={{ input: { textAlign: 'center' } }}
                              variant="standard"
                              type="number"
                              value={textFieldValue(c, metaCat)}
                              onChange={
                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                  scoreChangeHandler(e, c, metaCat);
                                }
                              }
                            />
                          )
                        }
                      </StyledTableCell>
                    ))
                  }
                </TableRow>
              ))
            }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FaerbungReviewTable;
